import { Avatar, Button, Checkbox } from '../../UIKit';
import { Field, Form, Formik } from 'formik';
import classnames from 'classnames';
import { TextField } from 'formik-material-ui';
import { TFunction } from 'i18next';
import React from 'react';
import { customerValidationScheme } from '../../Helpers/Validators.helper';
import { ICreateCustomer } from '../../Models/DTO/Customer.model';
import { ImageUpload } from '../ImageUpload';
import { UploadedImage } from '../../Models/interfaces/UploadedImage.model';
import { getInitials } from '../../Helpers/String.helper';
import { PLAIN_GREY } from '../../Constants/Colors.constants';
import { Segment } from '../../Helpers/Segment.helper';
import { makeStyles } from '~/UIKit/Style';

export interface CreateCustomerFormProps {
  t: TFunction;
  createCustomer: (data: ICreateCustomer) => void;
  toggleSendInvite: (sendInvite: boolean) => void;
  sendInvite: boolean;
  customerName: string;
  uploadedImage: UploadedImage;
}

const INITIAL_VALUES = (customerName: string) => ({
  firstname: customerName.split(' ')[0],
  lastname: customerName.split(' ')[1],
  email: '',
  phone: '',
  domain: '',
});

const useStyles = makeStyles({
  textField: {
    color: PLAIN_GREY,
    height: 51,
    '&:not(:last-child)': {
      marginBottom: 20,
    },
  },
  mandatoryBackground: {
    backgroundColor: 'rgba(56,62,65,0.05)',
  },
  sendInviteRoot: {
    display: 'flex',
    alignItems: 'center',
    '& label': {
      lineHeight: '21px',
    },
  },
  checkbox: {
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  imageUpload: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
  },
  avatar: {
    width: 80,
    height: 80,
    marginRight: 15,
    fontSize: 16,
    fontWeight: 'bold',
  },
  submitButton: {
    marginTop: 50,
    width: '100%',
  },
});

export default function CreateCustomerForm({
  t,
  createCustomer,
  customerName,
  uploadedImage,
  toggleSendInvite,
  sendInvite,
}: CreateCustomerFormProps) {
  const { classes } = useStyles();

  Segment.pageview('page_customer_create', location.pathname);

  const handleInviteToggle = () => {
    toggleSendInvite(!sendInvite);
  };

  return (
    <Formik
      initialValues={INITIAL_VALUES(customerName)}
      onSubmit={createCustomer}
      validationSchema={customerValidationScheme}
    >
      {({ values, errors }) => (
        <Form>
          <Field
            className={classnames(classes.textField, classes.mandatoryBackground)}
            component={TextField}
            fullWidth
            id="firstname"
            label={`${t('app.firstName')}*`}
            name="firstname"
            placeholder={`${t('app.firstName')}*`}
            type="text"
          />
          <Field
            className={classnames(classes.textField, classes.mandatoryBackground)}
            component={TextField}
            fullWidth
            id="lastname"
            label={`${t('app.lastName')}*`}
            name="lastname"
            placeholder={`${t('app.lastName')}*`}
            type="text"
          />
          <Field
            className={classes.textField}
            component={TextField}
            fullWidth
            id="email"
            label={t('app.customer.email')}
            name="email"
            type="text"
          />
          <Field
            className={classes.textField}
            component={TextField}
            fullWidth
            id="phone"
            label={t('app.customer.phone')}
            name="phone"
            type="text"
          />
          <div className={classes.sendInviteRoot}>
            <Checkbox
              checked={sendInvite}
              className={classes.checkbox}
              color="primary"
              disabled={
                values.email === undefined ||
                values.email.length === 0 ||
                errors.email !== undefined
              }
              id="sendInvite"
              name="sendInvite"
              onChange={handleInviteToggle}
            />
            <label htmlFor="sendInvite">{t('app.customer.sendInvite')}</label>
          </div>
          <div className={classes.imageUpload}>
            <Avatar
              alt={getInitials(customerName, '')}
              className={classes.avatar}
              src={uploadedImage.url}
            >
              {getInitials(customerName)}
            </Avatar>
            <ImageUpload
              canvasStyle={{
                width: 300,
                height: 300,
                color: [0, 0, 0, 0.4],
                borderRadius: 500,
                border: 10,
              }}
            />
          </div>
          <Button
            data-testid="customer.create"
            className={classes.submitButton}
            color="primary"
            type="submit"
            variant="contained"
          >
            {t('app.customer.create')}
          </Button>
        </Form>
      )}
    </Formik>
  );
}
